import React, { useEffect, useState } from 'react';
import { Platform, View, KeyboardAvoidingView, StatusBar, useWindowDimensions, DeviceEventEmitter, Pressable, TouchableOpacity, VirtualizedList, ActivityIndicator, RefreshControl } from 'react-native';
import { ThemeConsumer } from "@rneui/themed";
import Popover from 'react-native-popover-view/dist/Popover';

import { config } from '../../shared/environment';

import CommonHeader from '../../shared/commonComponents/commonHeader';
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox';

import { brandDataProps, setBrandProps } from './brandModule';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useAppNavigation } from '../../types/@react-navigation/navigation';
import { useKeyborad } from '../../shared/hooks/useKeyborad';
import { deletePosBrand, getAllBrands } from '../../redux/slices/brandSlice';

import { ZenBadge, ZenCheckBox, ZenEmptyComponent, ZenFilterModal, ZenIcon, ZenLoader, ZenText } from 'zen-core-elements';
import { filterItemsProps } from 'zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal';
import { shallowEqual } from 'react-redux';
import { ListToolbar } from '../../shared/commonComponents/listToolbar';
import { SortOption } from '../../shared/commonComponents/commonSortModal';

interface filterPayload { "medium"?: "WEB" | "POS", "visibility"?: | "POS" | "WEB" | "None" | "Both" }

function BrandList(): React.JSX.Element {
    // Variable to hold the width of the device
    const { width } = useWindowDimensions();
    const limit = width > 600 ? 20 : 10
    const navigation = useAppNavigation()
    const dispatch = useAppDispatch()
    const isKeyboardOpen = useKeyborad()
    const { value: brandData, count, loader } = useAppSelector((state) => state?.brands?.brandList)
    const { isWrite: isBrandWrite } = useAppSelector(getScreenPermission("BrandStack"), shallowEqual)
    // const [isLoading, setIsLoading] = useState<boolean>(true)
    //variable used to handle searchBar
    const [searchText, setSearchText] = useState<string | null>(null)
    //Variable to mainatin offset value and limit of virtualized list
    const [offset, setOffset] = useState<number | null>(null)
    //Variable to control footer loader
    const [footerLoader, setFooterLoader] = useState(false)
    //variable to handle delete popover
    const [deletePopoverVisible, setDeletePopoverVisible] = useState<{ isVisible: boolean, id?: number }>({ isVisible: false, })
    //Variable to maintain refresh control
    const [refresh, setRefresh] = useState<boolean>(false);
    const [selectedSortOption, setSelectedSortOption] = useState({ value: "", index: -1 })

    //variable to store selected filter
    const [isVisibleFilter, setIsVisibleFilter] = useState(false)
    //variable to store filter history data
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: '1',
            filterName: 'Medium',
            filterType: "radio",
            filterValue: [
                {
                    value: 'WEB',
                    label: 'Admin',
                },
                {
                    value: 'POS',
                    label: 'POS',
                }
            ],
            initialSelected: ''
        }
    ])
    //variable to store filter count
    const [filtercount, setFilterCount] = useState(0)
    const [filterpayload, setFilterPayload] = useState<string | null>(null)
    const [selectedBrand, setSelectedBrand] = useState<Set<number>>(new Set([]))


    //Variable to maintain sort values to display
    const sortOptionsData: SortOption[] = [
        { label: 'Brand name A-Z', column: 'brand_name', order: 'ASC', id: 1 },
        { label: 'Brand name Z-A', column: 'brand_name', order: 'DESC', id: 2 },
        { label: 'Recently created first', column: 'created_at', order: 'DESC', id: 3 },
        { label: 'Recently created last', column: 'created_at', order: 'ASC', id: 4 },
        { label: 'Recently updated first', column: 'modified_at', order: 'DESC', id: 5 },
        { label: 'Recently updated last', column: 'modified_at', order: 'ASC', id: 6 }
    ]
    const reInitializeStates = () => {
        setSelectedSortOption({ value: "", index: -1 })
        DeviceEventEmitter?.emit("brandList")
        setSelectedBrand(new Set([]))
        setFilterPayload(null)
        setSearchText(null);
    }
    useEffect(() => {
        const brandUpdate = DeviceEventEmitter.addListener("BRAND_UPDATED", () => {
            reInitializeStates()
        })
        return () => {
            brandUpdate?.remove()
        }
    }, [])

    // useEffect to trigger filterzone whenever text is searched
    useEffect(() => {
        if (searchText != null) {
            const searchDebounceFunction = setTimeout(() => {
                toggleOffset()
            }, 1000)
            return () => clearTimeout(searchDebounceFunction)
        }
    }, [searchText]);

    const toggleOffset = () => {
        setOffset((prev) => prev === 0 ? -1 : 0)
    }

    useEffect(() => {
        toggleOffset()
    }, [])

    useEffect(() => {
        if (offset != null) {
            getAllPosBrands()
        }
    }, [offset])

    useEffect(() => {
        if (filterpayload != null) {
            toggleOffset()
        }
    }, [filterpayload])

    const getAllPosBrands = async () => {
        const data: brandDataProps = {
            offset: offset === -1 ? 0 : offset || 0,
            limit: limit,
            searchText: searchText ? searchText.trim() : '',
            sortOrder: selectedSortOption?.value || JSON.stringify({ "field": "brand_name", "order": "ASC" }),
            filterData: filterpayload || null
        };
        await dispatch(getAllBrands({ storeId: config.storeId, data: data })).unwrap().then((res) => {
            if (res) {
                setFooterLoader(false)
                setRefresh(false)
            }
        }).catch((err: string) => {
            console.log("getAllPosBrands err", err)
        })
    }


    //Function to be called while removing a item from brandList
    const deleteBrand = async (id: number | number[]) => {
        await dispatch(deletePosBrand({ storeId: config.storeId, id })).unwrap().then((res) => {
            setSelectedBrand(new Set([]))
            toggleOffset()
        })
    }

    const deleteConfirmation = (value: { id: number }) => {
        let id = deletePopoverVisible?.id || (selectedBrand?.size > 1 ? [...selectedBrand] : [...selectedBrand][0])
        setDeletePopoverVisible({ isVisible: false })
        if (value?.id == 2) {
            deleteBrand(id)
        }
    }

    const handleCheckbox = (id: number, selectAll?: boolean) => {
        if (id !== -1) {
            setSelectedBrand((prev) => {
                let data = new Set([...prev])
                if (data.has(id)) {
                    data.delete(id)
                } else {
                    data.add(id)
                }
                return data
            })
        } else {
            let selectedCustomers: Set<number> = new Set([])
            if (selectAll) {
                brandData?.forEach((item) => selectedCustomers.add(item?.id))
            }
            setSelectedBrand(selectedCustomers)
        }
    }

    const renderItem = ({ item, index }: { item: setBrandProps[], index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={[{ flexDirection: "row" }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandRow]}>
                        {item?.map((brand: setBrandProps, ResponseDataIndex: number) => (
                            <View key={ResponseDataIndex} style={[{ flex: 1 / (width > 600 ? 2 : 1) }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandCardCell]}>
                                <Pressable style={[{
                                    marginBottom: 10,
                                    marginHorizontal: 10,
                                    padding: 10,
                                    borderRadius: 5,
                                    backgroundColor: theme.light
                                }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandCard, width < 600 && selectedBrand?.has(brand?.id) && { borderWidth: 1, borderColor: theme?.grey }]}
                                    onLongPress={() => handleCheckbox(brand?.id)}
                                    onPress={() => {
                                        if (width > 600 || (width < 600 && selectedBrand?.size))
                                            handleCheckbox(brand?.id)
                                    }}
                                >
                                    <View style={[{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandCardBody]}>
                                        <View style={[{ flexDirection: "row", alignItems: "flex-start", gap: 10 }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandInfo]}>
                                            {isBrandWrite && <View style={{ display: width > 600 ? "flex" : "none" }}>
                                                <ZenCheckBox
                                                    containerStyle={{ backgroundColor: "transparent" }}
                                                    checkedColor={theme.primary}
                                                    onPress={() => {
                                                        handleCheckbox(brand?.id)
                                                    }}
                                                    checked={selectedBrand?.has(brand?.id)}
                                                    size={24}
                                                />
                                            </View>}
                                            <View style={[{ width: "70%", gap: 5 }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandInfo]}>
                                                <View style={{ gap: 5, flexDirection: "row", alignItems: "flex-start" }}>
                                                    <ZenText content={"Brand Name:"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                                    <ZenText content={brand?.brandName} size={theme?.smallFontSize} style={[{ fontFamily: theme?.bold, color: theme?.lightDark, top: -2 }, Platform.OS === 'web' && theme?.brandListStyle?.webBrandName]} />
                                                </View>
                                                <View style={{ gap: 5, flexDirection: "row", alignItems: "flex-start" }}>
                                                    <ZenText content={"Count:"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} />
                                                    <ZenText content={brand?.productCount || 0} size={theme?.smallHeaderFontSize} style={{ fontFamily: theme?.medium, color: theme?.lightDark }} />
                                                </View>
                                                <View style={{ gap: 5, flexDirection: "row", alignItems: "center" }}>
                                                    {/* <ZenText content={"Medium:"} size={theme?.errorFontSize} style={{ fontFamily: theme?.medium, color: theme?.grey }} /> */}
                                                    <ZenBadge
                                                        badgeName={brand?.medium}
                                                        badgeColor={theme?.badgeColor}
                                                        badgeTextColor={theme?.light}
                                                        badgeTextSize={"small"}
                                                        badgeTextStyle={{ fontFamily: theme?.bold, padding: 2 }}
                                                    />
                                                </View>
                                            </View>
                                        </View>
                                        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                                            <TouchableOpacity onPress={() => navigation?.navigate("AddEditBrand", { id: brand?.id })}>
                                                <ZenIcon
                                                    name='pencil'
                                                    type='octicon'
                                                    color={theme?.iconColor}
                                                    size={theme?.iconSize}
                                                    style={{
                                                        "padding": 10,
                                                        "backgroundColor": theme.lightBackgroundColor,
                                                        "borderRadius": 10
                                                    }}
                                                />
                                            </TouchableOpacity>
                                            {isBrandWrite && <TouchableOpacity onPress={() => {
                                                setDeletePopoverVisible({ isVisible: true, id: brand?.id })
                                            }}>
                                                <ZenIcon
                                                    name='delete-outline'
                                                    type='material-community'
                                                    color={theme?.red}
                                                    size={theme?.iconSize}
                                                    style={{
                                                        "padding": 10,
                                                        "backgroundColor": theme.lightBackgroundColor,
                                                        "borderRadius": 10
                                                    }}
                                                />
                                            </TouchableOpacity>}
                                        </View>
                                    </View>
                                </Pressable>
                            </View>
                        ))}
                    </View>
                )
                }
            </ThemeConsumer >
        )
    }

    const getItem = (item: any[], index: number) => {
        let array: any = [];
        let numOfColumns = width > 600 ? 2 : 1
        for (let i = 0; i < numOfColumns; i++) {
            let temp = item?.[index * numOfColumns + i];
            temp && array?.push(temp);
        }
        return array;
    };

    const footerComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    footerLoader ?
                        <View>
                            <ActivityIndicator size={"small"} color={theme?.primary} />
                        </View >
                        : undefined
                )}
            </ThemeConsumer>
        )
    }

    const onRefresh = () => {
        setRefresh(true)
        reInitializeStates()
        toggleOffset()
    }
    const onEndReached = () => {
        let offsetValue = offset && offset != -1 ? offset : 0
        if (!footerLoader && (count!! > offsetValue)) {
            setFooterLoader(true)
            setOffset(offsetValue + limit)
        }
    }
    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView style={{ flex: 1, backgroundColor: "#f8f8f8", paddingBottom: 10 }}>
                    <StatusBar backgroundColor={theme.primary} />
                    <CommonHeader headerName='Brands' />
                    <View style={{ gap: 10, flex: 1, paddingTop: 10 }}>

                        <ListToolbar
                            search={{
                                placeholder: "Search Brand",
                                value: searchText ?? "",
                                onChange: (val) => setSearchText(val as string),
                                onCancel: () => setSearchText(null),
                            }}
                            sort={{
                                selectedId: selectedSortOption?.index,
                                onSelect: (item: SortOption) => {
                                    setSelectedSortOption({ value: JSON.stringify({ "field": item?.column, "order": item?.order }), index: item?.id! })
                                    toggleOffset()
                                },
                                title: 'Sort by',
                                options: sortOptionsData
                            }}
                            filter={{
                                count: filtercount ?? 0,
                                onPress: () => setIsVisibleFilter(true),
                            }}
                            buttons={isBrandWrite ? [{
                                onPress: () => navigation?.navigate("AddEditBrand", {}),
                                title: `Add Brand`,
                                variant: "primary"
                            }] : []}
                        />
                        {(loader && !refresh && !footerLoader) ?
                            <ZenLoader lottie={require('../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} /> :
                            <View style={{ flex: 1 }}>
                                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 10, display: selectedBrand?.size ? "flex" : "none", paddingBottom: 10 }}>
                                    <ZenCheckBox
                                        title={selectedBrand?.size === brandData.length ? "DeSelect All" : "Select All"}
                                        titleStyle={{ fontSize: theme.smallerFontSize }}
                                        containerStyle={{ marginLeft: 5, backgroundColor: "transparent" }}
                                        checkedColor={theme.primary}
                                        onPress={() => {
                                            if (selectedBrand?.size === brandData.length)
                                                handleCheckbox(-1, false)
                                            else
                                                handleCheckbox(-1, true)
                                        }}
                                        checked={selectedBrand?.size === brandData.length}
                                        size={20}
                                    />
                                    <TouchableOpacity style={theme?.customerListStyle?.optionInnerContainer} onPress={() => { setDeletePopoverVisible({ isVisible: true }) }}>
                                        <ZenText content={'Delete'} bold style={[theme?.customerListStyle?.optionTextStyle, { color: theme?.red }]} />
                                    </TouchableOpacity>
                                </View>
                                <VirtualizedList
                                    keyExtractor={(_: any, index: number) => index?.toString()}
                                    getItemCount={(data: any) => data?.length}
                                    getItem={getItem}
                                    scrollEnabled={true}
                                    nestedScrollEnabled={true}
                                    renderItem={renderItem}
                                    data={brandData || []}
                                    keyboardDismissMode="on-drag"
                                    showsVerticalScrollIndicator={false}
                                    onEndReachedThreshold={0.01}
                                    contentContainerStyle={[{ paddingBottom: brandData.length ? 10 : undefined, flex: brandData?.length ? undefined : 1 }]}
                                    ListFooterComponent={footerComponent}
                                    ListFooterComponentStyle={{ height: brandData.length ? 70 : 0 }}
                                    ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: theme?.light }} />}
                                    onEndReached={onEndReached}
                                    refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
                                />
                            </View>
                        }
                    </View>
                    {
                        width < 600 && !isKeyboardOpen && isBrandWrite &&
                        <Pressable style={{ position: "absolute", backgroundColor: theme.primary, width: 50, height: 50, borderRadius: 32, bottom: 40, right: 40, justifyContent: 'center', alignItems: "center" }}
                            onPress={() => { navigation?.navigate("AddEditBrand", {}) }}>
                            <ZenIcon type="material-community" name={"plus"} size={24} color={theme.light} />
                        </Pressable>
                    }
                    <ZenFilterModal
                        filterName='brandList'
                        setVisible={setIsVisibleFilter}
                        visible={isVisibleFilter}
                        filterItems={filterData}
                        setFilterItems={setFilterData}
                        filterCount={filtercount}
                        setFilterCount={setFilterCount}
                        title="Brand List Filter"
                        isSingleExpand={false}
                        onConfirm={(val) => {
                            let data: filterPayload = {}
                            if (val?.values?.Medium) {
                                data.medium = val?.values?.Medium
                            }
                            if (val?.values?.Visibility) {
                                data.visibility = val?.values?.Visibility
                            }
                            if (Object?.keys(data)?.length) {
                                setFilterPayload(JSON.stringify(data))
                            }
                            else {
                                setFilterPayload(null)
                                toggleOffset()
                            }

                        }
                        }
                    />
                    <Popover isVisible={deletePopoverVisible?.isVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
                        onRequestClose={() => setDeletePopoverVisible({ isVisible: false })}>
                        <CommonDialogBox type='DELETE' replaceValue={selectedBrand?.size > 1 ? 'brands' : 'brand'} confirmation={deleteConfirmation} />
                    </Popover>

                </KeyboardAvoidingView >
            )
            }
        </ThemeConsumer >
    );
}

export default BrandList;